import { HttpErrorResponse } from '@angular/common/http';
import { Action, createAction, createActionGroup, emptyProps, props } from '@ngrx/store';
import { FCPActivationDetails } from '../dtos/fcpActivationDetails.interface';
import { GetInvitationDto } from '../dtos/getInvitationDto.interface';
import { GetOrganizationDto } from '../dtos/getOrganizationDto.interface';
import { GetUserDto } from '../dtos/getUserDto.type';
import { InvitationsDto } from '../dtos/invitationsDto.interface';
import { OrganizationSearchFilterDto, OrganizationSearchResultDto } from '../dtos/organizationSearchDto.interface';
import { referralDto } from '../dtos/referralDto.interface';
import { UpdateOrganizationDto } from '../dtos/updateOrganizationDto.interface';
import { CPMMState } from '../enums/cpmm-state.enum';
import { FCPState } from '../enums/subscription-state.enum';
import { CPMMActivationDetails } from '../interfaces/cpmm-activation-details.interface';
import { OrganizationSettings } from '../interfaces/organization-settings.interface';
import { UpdateOrganizationAbcpDto } from '../interfaces/update-organization-abcp-request.interface';
import { UpdateOrganizationLocaleDto } from '../interfaces/update-organization-locale-request.interface';
import { IUpdateOrganizationState } from '../interfaces/update-organization-state-request.interface';
import { UpdateOrganizationTierDto } from '../interfaces/update-organization-tier-request.interface';
import { IUploadOrganizationReportingTemplateDto } from '../interfaces/upload-organization-reporting-template-dto.interface';

/**
 * The createAction function returns a function, that when called returns an object in the shape
 * of the Action interface. The props method is used to define any additional metadata needed
 * for the handling of the action.
 */

export const getCurrentOrganization = createActionGroup({
    source: '[Aumico-Users] GetCurrentOrganization',
    events: {
        do: emptyProps(),
        success: props<{ payload: GetOrganizationDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const updateCurrentOrganization = createActionGroup({
    source: '[Aumico-Users] UpdateCurrentOrganization',
    events: {
        do: props<{ payload: UpdateOrganizationDto }>(),
        success: props<{ payload: UpdateOrganizationDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const setCurrentOrganization = createAction(
    '[Aumico-AnnualReport] setCurrentOrganization',
    props<{ payload: Partial<GetOrganizationDto> }>(),
);

export const resetCurrentOrganization = createAction('[Aumico-AnnualReport] resetCurrentOrganization');

export const getAllOrganizations = createActionGroup({
    source: '[Aumico-Users] Get Organization For Management',
    events: {
        do: emptyProps(),
        success: props<{ payload: GetOrganizationDto[] }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});
export const searchOrganizations = createActionGroup({
    source: '[Aumico-Users] Search Organizations',
    events: {
        search: props<{ payload: OrganizationSearchFilterDto }>(),
        success: props<{ payload: OrganizationSearchResultDto[] }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});
export const createIndexAction = createActionGroup({
    source: '[Aumico-Users] Create Index For Searching Organizations',
    events: {
        do: emptyProps(),
        success: props<{ payload: void }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});
export const updateOrganizationState = createActionGroup({
    source: '[Aumico-Users] Update Organization State',
    events: {
        do: props<{ payload: IUpdateOrganizationState }>(),
        success: props<{ payload: IUpdateOrganizationState }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});
export const updateOrganizationTier = createActionGroup({
    source: '[Aumico-Users] Update Organization Tier',
    events: {
        do: props<{ payload: UpdateOrganizationTierDto }>(),
        forOwnOrg: props<{ payload: UpdateOrganizationTierDto }>(),
        success: props<{ payload: UpdateOrganizationTierDto }>(),
        successForOwnOrg: props<{ payload: UpdateOrganizationTierDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});
export const updateOrganizationLocale = createActionGroup({
    source: '[Aumico-Users] Update Organization Locale',
    events: {
        do: props<{ payload: UpdateOrganizationLocaleDto }>(),
        success: props<{ payload: UpdateOrganizationLocaleDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});
export const updateOrganizationAbcp = createActionGroup({
    source: '[Aumico-Users] Update Organization Abcp',
    events: {
        do: props<{ payload: UpdateOrganizationAbcpDto }>(),
        success: props<{ payload: UpdateOrganizationAbcpDto }>(),
        failure: props<{ payload: UpdateOrganizationAbcpDto; error: HttpErrorResponse }>(),
    },
});
export const updateOrganizationSettings = createActionGroup({
    source: '[Aumico-Users] Update Organization Settings',
    events: {
        do: props<{ payload: OrganizationSettings }>(),
        success: props<{ payload: OrganizationSettings }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});
export const getOrganizationSettings = createActionGroup({
    source: '[Aumico-Users] Get Organization Settings',
    events: {
        do: emptyProps(),
        success: props<{ payload: OrganizationSettings }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});
export const getUsers = createActionGroup({
    source: '[Aumico-Users] GetUsers',
    events: {
        do: props<{ includeDeleted?: boolean }>(),
        success: props<{ payload: GetUserDto[] }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const getUserById = createActionGroup({
    source: '[Aumico-Users] GetUsersById',
    events: {
        do: props<{ payload: string }>(),
        success: props<{ payload: GetUserDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const inviteUsers = createActionGroup({
    source: '[Aumico-Users] InviteUsers',
    events: {
        do: props<{ payload: string[] }>(),
        success: props<{ payload: InvitationsDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
        withdraw: props<{ payload: string[] }>(),
        withdrawSuccess: props<{ payload: string[] }>(),
        withdrawFailure: props<{ payload: HttpErrorResponse }>(),
        resendInvitation: props<{ payload: string }>(),
        resendInvitationSuccess: props<{ oldUuid: string; newInvitation: GetInvitationDto }>(),
        resendInvitationFailure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const inviteReferrals = createActionGroup({
    source: '[Aumico-Users] inviteReferrals',
    events: {
        do: props<{ payload: referralDto }>(),
        success: props<{ payload: referralDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
        accept: props<{ payload: string }>(),
        'Accept Referral Invitation Success': props<{ payload: string }>(),
        'Accept Referral Invitation Failure': props<{ payload: HttpErrorResponse }>(),
    },
});
export const removeUsers = createActionGroup({
    source: '[Aumico-Users] RemoveUsers',
    events: {
        do: props<{ payload: string[] }>(),
        success: props<{ payload: string[] }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const updateUserRole = createActionGroup({
    source: '[Aumico-Users] Update User Role',
    events: {
        do: props<{ payload: { userId: string; role: string } }>(),
        success: props<{ payload: GetUserDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const bulkUpdateUserRole = createActionGroup({
    source: '[Aumico-Users] Bulk Update User Role',
    events: {
        do: props<{ payload: { userIds: string[]; role: string } }>(),
        success: props<{ payload: GetUserDto[] }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const uploadOrganizationOrReportingTemplate = createActionGroup({
    source: '[Aumico-Users] Upload Organization Or Reporting Template',
    events: {
        do: props<{ payload: IUploadOrganizationReportingTemplateDto }>(),
        success: props<{ payload: IUploadOrganizationReportingTemplateDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const uploadOrganizationHgbReportingTemplate = createActionGroup({
    source: '[Aumico-Users] Upload Organization Hgb Reporting Template',
    events: {
        do: props<{ payload: IUploadOrganizationReportingTemplateDto }>(),
        success: props<{ payload: IUploadOrganizationReportingTemplateDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const setOrganizationReportingTemplateToDefault = createActionGroup({
    source: '[Aumico-Users] Set Organization Reporting Template To Default',
    events: {
        do: props<{ payload: string }>(),
        success: emptyProps(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const setOrganizationHgbReportingTemplateToDefault = createActionGroup({
    source: '[Aumico-Users] Set Organization Hgb Reporting Template To Default',
    events: {
        do: props<{ payload: string }>(),
        success: emptyProps(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const uploadOrganizationOrExcelTemplate = createActionGroup({
    source: '[Aumico-Users] Upload Organization Or Excel Template',
    events: {
        do: props<{ payload: IUploadOrganizationReportingTemplateDto }>(),
        success: props<{ payload: IUploadOrganizationReportingTemplateDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const uploadOrganizationHgbExcelTemplate = createActionGroup({
    source: '[Aumico-Users] Upload Organization Hgb Excel Template',
    events: {
        do: props<{ payload: IUploadOrganizationReportingTemplateDto }>(),
        success: props<{ payload: IUploadOrganizationReportingTemplateDto }>(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const setOrganizationExcelTemplateToDefault = createActionGroup({
    source: '[Aumico-Users] Set Organization Excel Template To Default',
    events: {
        do: props<{ payload: string }>(),
        success: emptyProps(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const setOrganizationHgbExcelTemplateToDefault = createActionGroup({
    source: '[Aumico-Users] Set Organization Hgb Excel Template To Default',
    events: {
        do: props<{ payload: string }>(),
        success: emptyProps(),
        failure: props<{ payload: HttpErrorResponse }>(),
    },
});

export const fcpManagement = createActionGroup({
    source: '[Aumico-Organization] FCP Management',
    events: {
        'ask for fcp approval': emptyProps(),
        'ask for fcp approval success': props<{ fcpActivation: FCPActivationDetails }>(),
        'ask for fcp approval failure': props<{ payload: HttpErrorResponse }>(),
        'deactivate fcp': props<{ surveyData: string }>(),
        'deactivate fcp success': props<{ fcpActivation: FCPActivationDetails }>(),
        'deactivate fcp failure': props<{ payload: HttpErrorResponse }>(),
        'change state fcp': props<{ organizationId: string; fcpState: FCPState }>(),
        'update fcp success': props<{ fcpActivation: FCPActivationDetails; organizationId: string }>(),
        'update fcp failure': props<{ payload: HttpErrorResponse }>(),
    },
});

export const cpmmActivation = createActionGroup({
    source: '[Aumico-Organization] CPMM ACTIVATION',
    events: {
        'change cpmm state': props<{ organizationId: string; cpmmState: CPMMState }>(),
        'change cpmm state success': props<{ cpmmActivation: CPMMActivationDetails; organizationId: string }>(),
        'change cpmm state failure': props<{ payload: HttpErrorResponse }>(),
    },
});

/**
 * Dispatch to pass payload, you can create parameterized Action in this way.
 */
export interface ActionWithPayload<T> extends Action {
    payload: T;
}
